import { AntDesign, Ionicons, MaterialIcons } from "@expo/vector-icons";
import { Box, HStack, Icon, Image, Pressable, Text, useTheme, VStack } from "native-base";
import React from "react";
import { StyleSheet } from "react-native";
import { useSelector } from "react-redux";

import { Scale, VerticalScale } from "../constants";
import { HORMONE_HEX_COLOR } from "../constants/theme";
import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import { getRecipeMealImage } from "../helpers/generalHelpers";
import { shouldShowHormoneIndicator } from "../helpers/userHelpers";
import type { GeneratedRecipeMeal, MealSlotSpecification } from "../services/backendTypes";
import { userSelector } from "../slices/userSlice";
import MacroProgressWidget from "./RecipeMacrosItem";

export const styles = StyleSheet.create({
  // Keep legacy styles for MacroProgressWidget compatibility
  recipeMacrosContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
});

type RecipeCardProps = {
  recipeMeal: GeneratedRecipeMeal;
  index: number;
  mealSlotSpecification: MealSlotSpecification;
  onButtonPress: () => void;
  onOpenAction?: () => void;
};

const RecipeMealCard = ({
  recipeMeal,
  index,
  onButtonPress,
  onOpenAction,
  mealSlotSpecification,
}: RecipeCardProps): JSX.Element => {
  const theme = useTheme();
  const user = useSelector(userSelector);

  // Check if recipe has HORMONE tag and if the hormone feature flag is active
  const showHormoneIndicator = shouldShowHormoneIndicator(user, recipeMeal.recipe_template.tags);
  const shouldHideMacros = isFeatureFlagActive(user, FeatureFlag.FF_DONT_SHOW_MACROS);

  return (
    <Pressable onPress={onButtonPress} nativeID={`recipeMeal-${index}`} flex={1}>
      <Box
        bg="white"
        borderRadius={theme.borderRadii.card.default}
        mx={1}
        mb={3}
        overflow="hidden"
        flex={1}
        shadow="10"
      >
        {/* Image Section */}
        <Box p={2}>
          <Box
            position="relative"
            width="100%"
            style={{ aspectRatio: 1 }}
            bg="gray.100"
            borderRadius={theme.borderRadii.card.default}
            overflow="hidden"
          >
            <Image
              source={getRecipeMealImage(recipeMeal).source}
              alt={recipeMeal.recipe_template.name}
              width="100%"
              height="100%"
              resizeMode="cover"
            />

            {/* Hormone Indicator */}
            {showHormoneIndicator ? (
              <Box
                position="absolute"
                bottom={3}
                left={3}
                bg="rgba(255, 255, 255, 0.95)"
                rounded="full"
                p={2}
                borderWidth={1.5}
                borderColor={HORMONE_HEX_COLOR}
                shadow={2}
              >
                <Icon as={Ionicons} name="female" size="md" color={HORMONE_HEX_COLOR} testID="hormoneIndicator" />
              </Box>
            ) : null}
          </Box>
        </Box>

        {/* Content Section */}
        <VStack
          px={2}
          pb={2}
          pt={0}
          space={1}
          testID={`recipeMeal-${recipeMeal.recipe_template.source_provider}-${
            recipeMeal.recipe_template.id || "MISSING_ID"
          }`}
        >
          {/* Recipe Name */}
          <Text
            fontSize="sm"
            fontWeight="semibold"
            numberOfLines={2}
            testID="recipeMealName"
            color="gray.800"
            lineHeight="sm"
          >
            {recipeMeal.recipe_template.name}
          </Text>

          {/* Preparation Time */}
          {Boolean(recipeMeal?.recipe_template?.preparation_time_min) && (
            <Text fontSize="xs" color="gray.500">
              {recipeMeal.recipe_template.preparation_time_min} mins
            </Text>
          )}
        </VStack>
      </Box>
    </Pressable>
  );
};

export default RecipeMealCard;
